import { Blac } from "blac-next";
import React, { useState } from "react";
import { BiometricVerificationBloc } from "src/hybrid/components/BiometricVerification";
import AppAccessCubit, {
  AppAccessStatus
} from "src/state/AppAccessCubit/AppAccessCubit";
import DateTimePicker from "../DateTimePicker/DateTimePicker";
import ExpectationsManagementScreen from "../ExpectationManagement/ExpectationsManagementScreen";
import RequestPushPermissionsCubit from "../GlobalOverlays/Overlays/RequestPushPermissionsCubit";
import AppDebugCubit from "./AppDebugCubit";
import { DebugButton, DebugButtonRow } from "./debug.styles";

// `emit` is protected on Cubit — for debug triggers we bypass that to force
// overlays open without replaying the conditions they normally gate on.
type StateForcer<S> = { emit: (next: S) => void };

const forceState = <S,>(cubit: object, next: S): void => {
  (cubit as unknown as StateForcer<S>).emit(next);
};

// Auto-collapse the debug panel after firing a trigger so the overlay we
// just opened is actually visible — otherwise the debug UI itself covers it.
const minifyDebugPanel = (): void => {
  Blac.getBloc(AppDebugCubit).setMinified(true);
};

const withMinify = (fn: () => void) => (): void => {
  fn();
  minifyDebugPanel();
};

const DebugOverlays: React.FC = () => {
  const [showExpectations, setShowExpectations] = useState(false);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);

  const forcePushPrompt = (): void => {
    forceState(Blac.getBloc(RequestPushPermissionsCubit), { show: true });
  };

  const dismissPushPrompt = (): void => {
    Blac.getBloc(RequestPushPermissionsCubit).handleDismiss();
  };

  const forceUnfinishedSignup = (): void => {
    // funnelRedirectPath left empty so the auto-redirect effect short-circuits
    // and the overlay stays visible for stack-testing.
    forceState(Blac.getBloc(AppAccessCubit), {
      status: AppAccessStatus.NEEDS_FUNNEL,
      funnelRedirectPath: undefined,
      funnelKey: undefined
    });
  };

  const dismissUnfinishedSignup = (): void => {
    Blac.getBloc(AppAccessCubit).reset();
  };

  const openBiometricSetup = (): void => {
    void Blac.getBloc(BiometricVerificationBloc).openSetupPrompt();
  };

  return (
    <div>
      <DebugButtonRow>
        <DebugButton onClick={withMinify(forcePushPrompt)}>
          Push Permissions
        </DebugButton>
        <DebugButton onClick={dismissPushPrompt}>Dismiss Push</DebugButton>
      </DebugButtonRow>
      <DebugButtonRow style={{ marginTop: "0.5rem" }}>
        <DebugButton onClick={withMinify(forceUnfinishedSignup)}>
          Unfinished Signup
        </DebugButton>
        <DebugButton onClick={dismissUnfinishedSignup}>
          Dismiss Unfinished
        </DebugButton>
      </DebugButtonRow>
      <DebugButtonRow style={{ marginTop: "0.5rem" }}>
        <DebugButton onClick={withMinify(openBiometricSetup)}>
          Biometric Setup
        </DebugButton>
      </DebugButtonRow>
      <DebugButtonRow style={{ marginTop: "0.5rem" }}>
        <DebugButton onClick={withMinify(() => setShowExpectations(true))}>
          Expectations Screen
        </DebugButton>
        <DebugButton onClick={withMinify(() => setShowDateTimePicker(true))}>
          Date Time Picker
        </DebugButton>
      </DebugButtonRow>

      {showExpectations && (
        <ExpectationsManagementScreen
          show
          onDismiss={() => setShowExpectations(false)}
        />
      )}
      {showDateTimePicker && (
        <DateTimePicker
          show
          title="Debug picker"
          granularity="minute"
          onFinish={() => setShowDateTimePicker(false)}
        />
      )}
    </div>
  );
};

export default DebugOverlays;
